import React, { useState, useEffect, Dispatch, SetStateAction } from 'react';
import {
  Table,
  Dropdown,
  InputGroup,
  FormControl,
  Button,
  Badge,
  Modal,
} from 'react-bootstrap';
import { FaPen } from 'react-icons/fa';
import './ProductValidation.scss';
import { getAllIngredientsByLocalId } from '../../../../../../../../../../crud/menu_api/ingredients';
import { getAllBrandsByLocalId }      from '../../../../../../../../../../crud/menu_api/brands';
import { MdOutlineArrowBackIos   } from 'react-icons/md';

export interface FormItem {
  item_index:       number;
  code:             string;
  systemId?:        number;
  systemName?:      string;
  facturaName:      string;
  cantidadInteres:  string;
  unidadInteres:    string;
  systemBrand?:     string;
  facturaBrand:     string;
  accepted:         boolean;
}

export interface ProductFound   { item_index:number; product_id:number; }
export interface ProductNotFound{ item_index:number;           }

export interface ProductsPrep {
  found:     ProductFound[];
  not_found: ProductNotFound[];
}

interface Props {
  localId:     number;
  products:    ProductsPrep;
  form:        FormItem[];
  setForm:     Dispatch<SetStateAction<FormItem[]>>;
  onReject:    (item_index: number) => void;
  onAccept:    (item_index: number, ingredient_id: number, ingredient_name: string) => void;
}

const UNIT_OPTIONS = [ 'kg','gr','L','mL','unid','pk','caja','dcn','cm','m','band','lb' ];

const ProductValidation: React.FC<Props> = ({
  localId,
  products,
  form,
  setForm,
  onReject,
  onAccept
}) => {
  // master lists
  const [ings,   setIngs]   = useState<{ingredient_id:number;ingredient_name:string}[]>([]);
  const [brands, setBrands] = useState<{brand_id:number;brand_name:string}[]>([]);

  // per-row brand edit flags
  const [editBr,  setEditBr ] = useState<Record<number,boolean>>({});

  // Modal state for ingredient selection/creation
  const [showIngModal, setShowIngModal] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [modalMode, setModalMode] = useState<'select' | 'create'>('select');
  const [selectedIngId, setSelectedIngId] = useState<number | null>(null);
  const [newIngName, setNewIngName] = useState('');

  useEffect(() => {
    getAllIngredientsByLocalId(localId).then((r: any) => setIngs(r.data));
    getAllBrandsByLocalId(localId).then((r: any) => setBrands(r.data));
  }, [localId]);

  const update = <K extends keyof FormItem>(idx:number, field:K, val: FormItem[K]) =>
    setForm(prev => prev.map(it =>
      it.item_index === idx ? ({ ...it, [field]: val } as FormItem) : it
    ));

  const updateMany = (idx:number, patch: Partial<FormItem>) =>
    setForm(prev => prev.map(it =>
      it.item_index === idx ? ({ ...it, ...patch }) : it
    ));

  const toggleAccept = (idx:number) =>
    update(idx,'accepted', !form.find(i=>i.item_index===idx)!.accepted);

  // Open modal for ingredient editing
  const openIngredientModal = (idx:number) => {
    const itm  = form.find(i => i.item_index === idx);
    const stub = products.found.find(f => f.item_index === idx);
    const suggestedId = itm?.systemId ?? (stub ? stub.product_id : undefined);
    const suggestedName = itm?.systemName
      ?? (stub ? ings.find(i => i.ingredient_id === stub.product_id)?.ingredient_name : undefined)
      ?? itm?.facturaName
      ?? '';

    setEditingIndex(idx);
    if (suggestedId != null) {
      setModalMode('select');
      setSelectedIngId(suggestedId);
    } else {
      setModalMode('create');
      setSelectedIngId(null);
    }
    setNewIngName(suggestedName);
    setShowIngModal(true);
  };

  // Save choice from modal
  const handleModalSave = () => {
    if (editingIndex === null) return;
    if (modalMode === 'create') {
      // User chose to create a new ingredient -> reject match and keep editable name
      updateMany(editingIndex, { systemId: undefined, systemName: newIngName, systemBrand: undefined });
      onReject(editingIndex);
    } else if (modalMode === 'select' && selectedIngId != null) {
      const ing = ings.find(i => i.ingredient_id === selectedIngId);
      if (ing) {
        updateMany(editingIndex, { systemId: ing.ingredient_id, systemName: ing.ingredient_name });
        onAccept(editingIndex, ing.ingredient_id, ing.ingredient_name);
      }
    }
    setShowIngModal(false);
    setEditingIndex(null);
    setSelectedIngId(null);
    setNewIngName('');
  };

  const handleModalCancel = () => {
    setShowIngModal(false);
    setEditingIndex(null);
    setSelectedIngId(null);
    setNewIngName('');
  };

  const pickBrand = (idx:number, b:string) => {
    update(idx,'systemBrand', b);
    setEditBr(prev => ({ ...prev, [idx]: false }));
  };

  return (
    <div className="product-validation">
      <h5>2. Validar Productos</h5>
      <Table hover bordered className="mb-0">
        <thead>
          <tr>
            <th></th>
            <th>Código</th>
            <th>Nombre</th>
            <th>Cantidad</th>
            <th>Unidad</th>
            <th>Marca</th>
          </tr>
        </thead>
        <tbody>
          {form.map(itm => {
            const idx    = itm.item_index;
            const stub   = products.found.find(f => f.item_index === idx);
            const fallback = stub
              ? ings.find(i => i.ingredient_id === stub.product_id)?.ingredient_name
              : undefined;

            // If there's a concrete match (systemId), allow fallback; otherwise never fall back
            const dispName = typeof itm.systemId === 'number'
              ? (itm.systemName ?? fallback)
              : (itm.systemName ?? itm.facturaName);
            const matched  = typeof itm.systemId === 'number';

            return (
              <tr key={idx}>
                <td className="text-center">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={itm.accepted}
                    onChange={()=>toggleAccept(idx)}
                  />
                </td>
                <td>
                  <FormControl
                    size="sm"
                    value={itm.code}
                    onChange={e=>update(idx,'code',e.target.value)}
                  />
                </td>
                <td className="dropdown-cell">
                  <div className="ingredient-display">
                    <span className={matched ? 'found-badge' : 'plain-name'}>
                      {dispName ?? itm.facturaName}
                    </span>
                    <Button
                      variant="none"
                      size="sm"
                      className="mp-button light"
                      onClick={()=>openIngredientModal(idx)}
                      title="Editar ingrediente"
                    >
                      <FaPen/>
                    </Button>
                  </div>
                </td>
                <td><FormControl size="sm" value={itm.cantidadInteres} onChange={e=>update(idx,'cantidadInteres',e.target.value)} /></td>
                <td>
                  <FormControl as="select" size="sm" value={itm.unidadInteres} onChange={e=>update(idx,'unidadInteres',e.target.value)}>
                    {UNIT_OPTIONS.map(u=>(<option key={u} value={u}>{u}</option>))}
                  </FormControl>
                </td>
                <td className="dropdown-cell">
                  {itm.systemBrand && !editBr[idx] ? (
                    <div className="brand-display">
                      <Badge bg="info" className="me-2">{itm.systemBrand}</Badge>
                      <div className="brand-button-group">
                        <Button variant="none" size="sm" className="mp-button light" onClick={()=>pickBrand(idx,'')}><MdOutlineArrowBackIos/></Button>
                        <Button variant="none" size="sm" className="mp-button light" onClick={()=>setEditBr(b=>({ ...b,[idx]:true }))}><FaPen/></Button>
                      </div>
                    </div>
                  ) : editBr[idx] ? (
                    <div className="brand-display">
                      <Dropdown>
                        <Dropdown.Toggle size="sm" variant="outline-info">{itm.systemBrand ?? 'Marca…'}</Dropdown.Toggle>
                        <Dropdown.Menu as="div" className="p-2 filter-menu">
                          <InputGroup size="sm" className="mb-1"><FormControl placeholder="Buscar marca"/></InputGroup>
                          {brands.map(b=>(
                            <Dropdown.Item key={b.brand_id} active={b.brand_name===itm.systemBrand} onClick={()=>pickBrand(idx,b.brand_name)}>
                              {b.brand_name}
                            </Dropdown.Item>
                          ))}
                        </Dropdown.Menu>
                      </Dropdown>
                      <Button variant="none" size="sm" className="mp-button light" onClick={()=>pickBrand(idx,'')}><MdOutlineArrowBackIos/></Button>
                    </div>
                  ) : (
                    <div className="brand-display">
                      <span className="plain-name">{itm.facturaBrand}</span>
                      <Button variant="none" size="sm" className="mp-button light" onClick={()=>setEditBr(b=>({ ...b,[idx]:true }))}><FaPen/></Button>
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>

      {/* Ingredient selection/creation modal */}
      <Modal className="ingredient-modal" show={showIngModal} onHide={handleModalCancel} centered>
        <Modal.Header closeButton>
          <Modal.Title>Seleccionar ingrediente</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalMode === 'select' ? (
            <>
              <div className="mb-3">
                <label className="form-label">Ingrediente</label>
                <FormControl
                  as="select"
                  value={selectedIngId ?? ''}
                  onChange={e => {
                    const v = e.target.value;
                    setSelectedIngId(v ? Number(v) : null);
                  }}
                >
                  <option value="" disabled>Seleccione…</option>
                  {ings.map(i => (
                    <option key={i.ingredient_id} value={i.ingredient_id}>{i.ingredient_name}</option>
                  ))}
                </FormControl>
              </div>
              <div>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => setModalMode('create')}
                >
                  Crear nuevo
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="mb-3">
                <label className="form-label">Nombre del nuevo ingrediente</label>
                <FormControl
                  placeholder="Nombre"
                  value={newIngName}
                  onChange={e => setNewIngName(e.target.value)}
                />
              </div>
              <div>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => setModalMode('select')}
                >
                  <MdOutlineArrowBackIos style={{ marginRight: 6 }} />
                  Volver a la lista
                </Button>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleModalCancel}>Cancelar</Button>
          <Button
            variant="primary"
            onClick={handleModalSave}
            disabled={modalMode === 'create' ? newIngName.trim().length === 0 : selectedIngId === null}
          >
            Guardar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ProductValidation;
